import { AbacoWebModule, ApplicationReadyEvent, RouterReadyEvent, VueRouter } from "@abaco/web-common/src/app";
import { BaseUser } from "@abaco/web-common/src/BaseUser";
import { HTTPClient } from "@abaco/web-common/src/HTTP";
import { setI18N } from "@abaco/web-components/src/components";
import "reflect-metadata";
import { abacoApp } from "../../main";
import ChangeRequestApi from "./api/ChangeRequestApi";
import DecodingApi from "./api/DecodingApi";
import MaltaApi from "./api/MaltaApi";
import PCRApi from "./api/PCRApi";
import PPRApi from "./api/PPRApi";
import ProjectProposalApi from "./api/ProjectProposalApi";
import globals from "./globals";
import myRoutes from "./routes";
import MaltaCustomerPortalVuexModule from "./state";
import TokensResources from "./TokensResources";

export const MODULE_ID = "malta_customer_portal";
export const PORTAL_TARGET = "footer_actions";

export const LOGIN_CONTEXT = "CUSTOMERPORTAL";

export default class MaltaCustomerPortalModule implements AbacoWebModule {
    public static MODULE_ID = MODULE_ID;
    private _mltApi!: MaltaApi;
    private _pcrApi!: PCRApi;
    private _pprApi!: PPRApi;
    private _crApi!: ChangeRequestApi;
    private _ppApi!: ProjectProposalApi;
    private _dcdApi!: DecodingApi;

    private readonly loginContext = LOGIN_CONTEXT;

    private router!: VueRouter;
    private http!: HTTPClient;

    private tokensRes!: TokensResources;
    private lastValidatedSso1Token = null as string | null;

    readonly id = MODULE_ID;
    readonly routes = myRoutes;
    readonly globalComponents = globals;
    readonly vuexModule = new MaltaCustomerPortalVuexModule();
    readonly forceTopLevelRoutes = true;

    additionalI18N?: string[] = ["web_components"];

    public addAnauthorizedHandler(http: HTTPClient) {
        const router = this.router;
        http.addErrorHandler({
            onResponseError(_, response) {
                if (response && response.status === 401) {
                    if (router.currentRoute.name !== "login") {
                        router
                            .push({ name: "login", replace: true, params: { redirectTo: router.currentRoute.path } })
                            .catch(e => {
                                console.warn(e);
                            });
                    }
                }
            }
        });
    }

    onRouterReady(evt: RouterReadyEvent) {
        this.router = evt.router;
        this.tokensRes = new TokensResources(evt.http, this.vuexModule, (user: BaseUser) => {
            setI18N({ locale: user.locale, dateFormat: user.dateFormat });
            abacoApp.setLanguage(user.locale);
        });

        this.addAnauthorizedHandler(evt.http);

        evt.router.beforeEach((to, _, next) => {
            const ssoToken = to.query.SSO_TOKEN as string | null;
            if (ssoToken && ssoToken !== this.lastValidatedSso1Token) {
                this.tokensRes.logout();
                this.tokensRes
                    .exchangeSso1Token(ssoToken, "")
                    .then(() => {
                        this.lastValidatedSso1Token = ssoToken;
                        next();
                    })
                    .catch(() => {
                        next({ name: "login", replace: true, params: { redirectTo: to.fullPath } });
                    });
                return;
            }

            const authRequired = to.matched.some(m => !m.meta?.guest);
            if (authRequired && !this.vuexModule.user) {
                if (!this.tokensRes.resumeSession()) {
                    next({ name: "login", replace: true, params: { redirectTo: to.fullPath } });
                    return;
                }
            }
            

            next();
        });
    }

    onApplicationReady(evt: ApplicationReadyEvent) {
        this.http = evt.http;
    }

    async beforeEnterAnyRoutes(http: HTTPClient) {
        this._mltApi = MaltaApi.getInstance(http);
        this._pcrApi = PCRApi.getInstance(http);
        this._pprApi = PPRApi.getInstance(http);
        this._crApi = ChangeRequestApi.getInstance(http);
        this._ppApi = ProjectProposalApi.getInstance(http);
        this._dcdApi = DecodingApi.getInstance(http);
    }

    public get maltaApi(): MaltaApi {
        return this._mltApi;
    }

    public get pcrApi(): PCRApi {
        return this._pcrApi;
    }

    public get pprApi(): PPRApi {
        return this._pprApi;
    }

    public get changeRequestApi(): ChangeRequestApi {
        return this._crApi;
    }

    public get projectProposalApi(): ProjectProposalApi {
        return this._ppApi;
    }

    public get decodingApi(): DecodingApi {
        return this._dcdApi;
    }

    public get tokensResources() {
        return this.tokensRes;
    }

    public async checkContext(): Promise<boolean> {
        if (!this.vuexModule.user) {
            return false;
        }

        const ctxs = await this.tokensResources.loadUserContexts();
        return ctxs.some(c => c === this.loginContext);
    }

    public async isFunctionEnabled(ctx: string, fun: string): Promise<boolean> {
        if (this.vuexModule.userFunctions === null) {
            await this.tokensResources.loadUserFunctions();
        }
        return this.vuexModule.userFunctions?.[ctx]?.includes(fun) || false;
    }
}
